import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ScrollProgress } from './motion/Motion';
import './Header.css';
import logoImg from '../assets/images/logo.png';

/** Ordem idêntica à da página — alimenta a navegação e o scrollspy. */
const NAV_ITEMS = [
  { id: 'inicio', label: 'Início' },
  { id: 'pilares', label: 'Nossos Pilares' },
  { id: 'vantagens', label: 'Vantagens e benefícios' },
  { id: 'tecnologia', label: 'A tecnologia ' },
  { id: 'nossas-solucoes', label: 'Nossas Soluções' },
  { id: 'depoimentos', label: 'Depoimentos' },
  { id: 'nos', label: 'Nós' },
  { id: 'missao', label: 'Missão, Visão e Valores' },
  { id: 'contato', label: 'Contato' },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduced = useReducedMotion();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState('inicio');

  const headerRef = useRef(null);
  const toggleRef = useRef(null);

  const isHome = location.pathname === '/';

  /* Estado sólido do header. O limiar é baixo de propósito: assim que a página
     sai do topo o fundo entra, e o logo volta à cor de marca. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Scrollspy. Só faz sentido na Home, que é quem tem as seções. */
  useEffect(() => {
    if (!isHome) return undefined;

    const sections = NAV_ITEMS
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    /* A faixa de detecção fica no terço superior da tela: a seção "ativa" é a
       que está sob o header, não a que ocupa mais pixels. */
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome, location.pathname]);

  /* Trava o scroll do body enquanto o menu mobile está aberto. */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [mobileOpen]);

  /* Esc fecha o menu — o clique fora é resolvido pelo próprio overlay. */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const scrollToSection = useCallback((sectionId) => {
    const doScroll = () => {
      const element = document.getElementById(sectionId);
      if (!element) return;
      const headerHeight = headerRef.current?.offsetHeight ?? 80;
      const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
      window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
    };

    setMobileOpen(false);

    if (!isHome) {
      navigate('/');
      /* A Home precisa montar antes de existir o elemento alvo. */
      setTimeout(doScroll, 160);
    } else {
      doScroll();
    }
  }, [isHome, navigate, reduced]);

  return (
    <>
      <header
        ref={headerRef}
        className={`site-header${scrolled ? ' is-scrolled' : ''}${mobileOpen ? ' is-menu-open' : ''}`}
      >
        <div className="header-inner">
          <Link to="/" className="logo-link" aria-label="iPoolCare — início">
            <img
              src={logoImg}
              alt="iPoolCare"
              className="logo-image"
              width={300}
              height={76}
            />
          </Link>

          <nav className="main-nav" aria-label="Navegação principal">
            {NAV_ITEMS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                className={`nav-link${isHome && activeId === id ? ' is-active' : ''}`}
                aria-current={isHome && activeId === id ? 'true' : undefined}
              >
                <span className="nav-link__text">{label}</span>
                {isHome && activeId === id && !reduced && (
                  <motion.span
                    className="nav-link__marker"
                    layoutId="nav-marker"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  />
                )}
              </button>
            ))}
          </nav>

          <button
            ref={toggleRef}
            type="button"
            className={`menu-toggle${mobileOpen ? ' is-open' : ''}`}
            onClick={() => setMobileOpen((open) => !open)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <span className="menu-toggle__bar" />
            <span className="menu-toggle__bar" />
            <span className="menu-toggle__bar" />
          </button>
        </div>

        <ScrollProgress className="header-progress" />
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setMobileOpen(false)}
          >
            <motion.nav
              id="mobile-nav"
              className="mobile-nav"
              aria-label="Navegação principal"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              {NAV_ITEMS.map(({ id, label }, index) => (
                <motion.button
                  key={id}
                  type="button"
                  className={`mobile-nav__link${isHome && activeId === id ? ' is-active' : ''}`}
                  onClick={() => scrollToSection(id)}
                  initial={{ opacity: 0, x: 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.045, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  {label}
                </motion.button>
              ))}
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
